// FieldVision - MarkDetaljePage.tsx
// Mark detalje side med split-view layout: sidebar + Leaflet kort
// FIX: Frontend fallback - proever begge Marknr formater hvis foerste kald returnerer tomt
// FIX: Haandterer satellit kort GeoJson korrekt
// FIX: Parser microservice FeatureCollection format korrekt (hver feature = en zone)
// NEW: Support for delete med refresh
// FIX: Sender nu CVR parameter til getZoneMapsByField for at undgaa cross-CVR data (2025-12-30)

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MarkDropdown from './MarkDropdown';
import NoteField from './NoteField';
import KortHistorik from './KortHistorik';
import ZoneMapView from './ZoneMapView';
import ZoneLegend from './ZoneLegend';
import apiService from '../services/api';
import { ZoneData } from './zones/ZoneRow';

export const ZONE_COLORS: Record<number, string> = {
  1: '#F61F1F',
  2: '#F68C1F',
  3: '#F6C81F',
  4: '#B9F61F',
  5: '#26F61F',
};

interface FieldMap {
  Id: number;
  FieldId: number | null;
  OrderId: number | null;
  Type: string;
  Source: string;
  CaptureDate: string;
  Status: string;
  ZonesJson?: string;
  Zones?: any;
  GeoJson?: any; // FIX: Tilfoej GeoJson property for satellit kort
  ThumbnailPath?: string;
  CreatedAt: string;
  Marknr: string;
  Areal: number | null;
  CVR: string;
}

interface Field {
  id?: string | number;
  marknr: string;
  areal: number;
  cvr: string;
  afgrode?: string;
  mapCount?: number;
  geometry?: string;
}

interface Order {
  orderId: number;
  orderNumber?: string;
  serviceType?: string;
  status?: string;
  fieldNames?: string[];
}

interface MarkDetaljePageProps {
  fieldId?: string;
  orderId?: number;
  field?: Field;
  order?: Order;
  fields?: Field[];
  onBack: () => void;
  onFieldChange?: (fieldId: string) => void;
  onNavigateToBooking?: () => void;
}

const MarkDetaljePage: React.FC<MarkDetaljePageProps> = ({
  fieldId,
  orderId,
  field,
  order,
  fields = [],
  onBack,
  onFieldChange,
  onNavigateToBooking
}) => {
  const [maps, setMaps] = useState<FieldMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeMapId, setActiveMapId] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [currentMarknr, setCurrentMarknr] = useState<string | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);

  useEffect(() => {
    console.log('=== MarkDetaljePage useEffect ===');
    console.log('fieldId:', fieldId);
    console.log('fieldId type:', typeof fieldId);
    console.log('orderId:', orderId);
    console.log('field prop:', field);
    console.log('field.cvr:', field?.cvr);
    loadMaps();
  }, [fieldId, orderId]);

  /**
   * FIX: Proever begge Marknr formater hvis foerste kald returnerer tomt
   * Backend burde haandtere begge formater, men som fallback proever vi begge fra frontend
   * FIX: Sender nu CVR parameter for at filtrere kort til specifik mark
   */
  const loadMaps = useCallback(async () => {
    console.log('=== MarkDetaljePage loadMaps START ===');
    console.log('fieldId:', fieldId);
    console.log('orderId:', orderId);
    console.log('field?.cvr:', field?.cvr);

    try {
      setLoading(true);
      setError(null);

      let allMaps: FieldMap[] = [];
      let marknr: string | null = null;

      // FIX: Hent CVR fra field prop hvis tilgaengelig
      const cvr = field?.cvr;

      if (orderId) {
        console.log('loadMaps: Loading via orderId', orderId);

        const orderMaps = await apiService.getZoneMapsByOrder(orderId);
        console.log('loadMaps: orderMaps response:', orderMaps);

        if ((orderMaps as any[]).length > 0) {
          marknr = (orderMaps as any[])[0].Marknr;
          console.log('loadMaps: Got marknr from order:', marknr);
          setCurrentMarknr(marknr);

          if (marknr) {
            console.log('loadMaps: Fetching field maps for marknr:', marknr, 'cvr:', cvr);
            // FIX: Send CVR med hvis tilgaengeligt
            const fieldMaps = await apiService.getZoneMapsByField(marknr as any, cvr);
            console.log('loadMaps: fieldMaps response:', fieldMaps);
            const mapIds = new Set((fieldMaps as any[]).map((m: any) => (m as any).Id));
            allMaps = [...(fieldMaps as any[])];

            for (const orderMap of (orderMaps as any[])) {
              if (!mapIds.has((orderMap as any).Id)) {
                allMaps.push(orderMap as FieldMap);
              }
            }
          } else {
            allMaps = orderMaps as FieldMap[];
          }
        }
      } else if (fieldId) {
        console.log('loadMaps: Loading via fieldId (Marknr):', fieldId, 'cvr:', cvr);
        setCurrentMarknr(fieldId);

        // FIX: Proev foerst med det originale fieldId og CVR
        console.log('loadMaps: Trying with original fieldId:', fieldId, 'cvr:', cvr);
        let fieldMaps = await apiService.getZoneMapsByField(fieldId as any, cvr);
        console.log('loadMaps: First API response:', fieldMaps);

        // FIX: Hvis tomt resultat, proev med "Mark " prefix
        if (!Array.isArray(fieldMaps) || fieldMaps.length === 0) {
          // Tjek om fieldId allerede har "Mark " prefix
          if (!fieldId.startsWith('Mark ')) {
            const marknrWithPrefix = 'Mark ' + fieldId;
            console.log('loadMaps: No results, trying with prefix:', marknrWithPrefix, 'cvr:', cvr);
            fieldMaps = await apiService.getZoneMapsByField(marknrWithPrefix as any, cvr);
            console.log('loadMaps: Prefixed API response:', fieldMaps);
          }
        }

        // FIX: Hvis stadig tomt og fieldId har "Mark " prefix, proev uden
        if (!Array.isArray(fieldMaps) || fieldMaps.length === 0) {
          if (fieldId.startsWith('Mark ')) {
            const marknrWithoutPrefix = fieldId.replace(/^Mark /, '');
            console.log('loadMaps: No results, trying without prefix:', marknrWithoutPrefix, 'cvr:', cvr);
            fieldMaps = await apiService.getZoneMapsByField(marknrWithoutPrefix as any, cvr);
            console.log('loadMaps: Unprefixed API response:', fieldMaps);
          }
        }

        allMaps = Array.isArray(fieldMaps) ? fieldMaps as FieldMap[] : [];
        console.log('loadMaps: Final allMaps.length:', allMaps.length);
      }

      // Sorter kort
      allMaps.sort((a: any, b: any) => {
        const typeOrder: Record<string, number> = {
          'NDVI': 1, 'ndvi': 1,
          'NDRE': 1, 'ndre': 1, // FIX: Tilfoej NDRE for satellit
          'weed': 2, 'Weed': 2, 'Ukrudt': 2,
          'regrowth': 3, 'Regrowth': 3
        };
        const typeA = typeOrder[(a as any).Type] || 99;
        const typeB = typeOrder[(b as any).Type] || 99;
        if (typeA !== typeB) return typeA - typeB;

        const dateA = new Date((a as any).CreatedAt || (a as any).CaptureDate).getTime();
        const dateB = new Date((b as any).CreatedAt || (b as any).CaptureDate).getTime();
        return dateB - dateA;
      });

      console.log('loadMaps: Setting maps state with', allMaps.length, 'maps');
      setMaps(allMaps);

      if (allMaps.length > 0) {
        const orderMap = orderId
          ? allMaps.find((m: any) => (m as any).OrderId === orderId)
          : null;
        setActiveMapId((orderMap as any)?.Id || (allMaps[0] as any).Id);
        console.log('loadMaps: Set activeMapId to:', (orderMap as any)?.Id || (allMaps[0] as any).Id);
      } else {
        setActiveMapId(null);
        console.log('loadMaps: No maps found, activeMapId = null');
      }

      console.log('=== MarkDetaljePage loadMaps END ===');
    } catch (err) {
      console.error('loadMaps ERROR:', err);
      setError('Kunne ikke hente kort');
    } finally {
      setLoading(false);
    }
  }, [fieldId, orderId, field?.cvr]);

  const activeMap = useMemo(() => {
    return maps.find((m: any) => (m as any).Id === activeMapId) || null;
  }, [maps, activeMapId]);

  /**
   * Parse zones fra forskellige formater:
   * 1. Array direkte: [{ zone: 1, hectares: ... }, ...]
   * 2. Object med zones property: { zones: [...] }
   * 3. Object med geojson.features: { geojson: { features: [...] } }
   * 4. FeatureCollection med zones i features[0].properties.zones (gammel satellit)
   * 5. FeatureCollection hvor hver feature ER en zone (ny microservice format)
   */
  const activeZones = useMemo((): ZoneData[] => {
    if (!activeMap) return [];

    let zones: ZoneData[] = [];

    // Helper function to parse FeatureCollection where each feature is a zone
    const parseFeatureCollectionZones = (fc: any): ZoneData[] => {
      if (!fc.features || !Array.isArray(fc.features)) return [];

      // Check if features have zone property (microservice format)
      const hasZoneProperty = fc.features.some((f: any) =>
        f.properties && typeof f.properties.zone === 'number'
      );

      if (hasZoneProperty) {
        console.log('activeZones: Parsing microservice FeatureCollection format (each feature = zone)');
        return fc.features
          .filter((f: any) => f.properties && typeof f.properties.zone === 'number')
          .map((f: any) => ({
            zone: f.properties.zone,
            hectares: f.properties.hectares || 0,
            rate: f.properties.rate || 0,
            color: f.properties.color || ZONE_COLORS[f.properties.zone],
            ndreMin: f.properties.ndreMin,
            ndreMax: f.properties.ndreMax
          }));
      }

      return [];
    };

    if ((activeMap as any).Zones) {
      if (Array.isArray((activeMap as any).Zones)) {
        zones = (activeMap as any).Zones;
      } else if ((activeMap as any).Zones.zones) {
        zones = (activeMap as any).Zones.zones;
      } else if ((activeMap as any).Zones.geojson?.features) {
        zones = (activeMap as any).Zones.geojson.features.map((f: any) => ({
          zone: (f as any).properties.zone,
          rate: (f as any).properties.rate,
          rateInt: (f as any).properties.rateInt,
          unit: (f as any).properties.unit || 'l/ha',
          hectares: (f as any).properties.hectares,
        }));
      }
      // FIX: Check for FeatureCollection where each feature is a zone (microservice format)
      else if ((activeMap as any).Zones.type === 'FeatureCollection') {
        zones = parseFeatureCollectionZones((activeMap as any).Zones);
      }
    } else if ((activeMap as any).ZonesJson) {
      try {
        const parsed = JSON.parse((activeMap as any).ZonesJson);
        if (Array.isArray(parsed)) {
          zones = parsed;
        } else if (parsed.zones) {
          zones = parsed.zones;
        } else if (parsed.geojson?.features) {
          zones = parsed.geojson.features.map((f: any) => ({
            zone: (f as any).properties.zone,
            rate: (f as any).properties.rate,
            rateInt: (f as any).properties.rateInt,
            unit: (f as any).properties.unit || 'l/ha',
            hectares: (f as any).properties.hectares,
          }));
        }
        // FIX: Haandter satellit format - FeatureCollection med zones i properties
        else if (parsed.type === 'FeatureCollection' && parsed.features?.[0]?.properties?.zones) {
          zones = parsed.features[0].properties.zones;
        }
        // FIX: Haandter ny microservice format - FeatureCollection hvor hver feature er en zone
        else if (parsed.type === 'FeatureCollection') {
          zones = parseFeatureCollectionZones(parsed);
        }
      } catch (e) {
        console.error('Error parsing ZonesJson:', e);
      }
    }

    // FIX: Also check GeoJson property (satellite maps may store zones there)
    if (zones.length === 0 && (activeMap as any).GeoJson) {
      const geoJson = (activeMap as any).GeoJson;
      if (geoJson.type === 'FeatureCollection') {
        zones = parseFeatureCollectionZones(geoJson);
      }
    }

    console.log('activeZones: Final parsed zones:', zones.length, 'zones');
    return zones.filter((z: any) => (z as any).zone !== 0).sort((a: any, b: any) => (a as any).zone - (b as any).zone);
  }, [activeMap]);

  // FIX: Opdateret activeGeoJson memo til at haandtere satellit kort
  const activeGeoJson = useMemo(() => {
    if (!activeMap) return null;

    // FIX: Check GeoJson property first (satellite format - backend setter dette)
    if ((activeMap as any).GeoJson) {
      console.log('activeGeoJson: Using activeMap.GeoJson (satellite format)');
      return (activeMap as any).GeoJson;
    }

    // Check Zones.geojson (drone format)
    if ((activeMap as any).Zones?.geojson) {
      console.log('activeGeoJson: Using activeMap.Zones.geojson (drone format)');
      return (activeMap as any).Zones.geojson;
    }

    // FIX: Check if Zones itself is a FeatureCollection (microservice format)
    if ((activeMap as any).Zones?.type === 'FeatureCollection') {
      console.log('activeGeoJson: Using activeMap.Zones as FeatureCollection (microservice format)');
      return (activeMap as any).Zones;
    }

    // Parse ZonesJson
    if ((activeMap as any).ZonesJson) {
      try {
        const parsed = JSON.parse((activeMap as any).ZonesJson);

        // Check for nested geojson property (drone format)
        if (parsed.geojson) {
          console.log('activeGeoJson: Using parsed.geojson (drone format)');
          return parsed.geojson;
        }

        // FIX: If it's a FeatureCollection, return it directly (satellite format)
        if (parsed.type === 'FeatureCollection') {
          console.log('activeGeoJson: Using parsed FeatureCollection directly (satellite format)');
          return parsed;
        }
      } catch (e) {
        console.error('Error parsing ZonesJson for geojson:', e);
      }
    }

    console.log('activeGeoJson: No GeoJson found');
    return null;
  }, [activeMap]);

  // Byg field outline GeoJSON naar ingen tildelingskort
  const fieldOutlineGeoJson = useMemo(() => {
    if (activeGeoJson) return null;
    if (!(field as any)?.geometry) return null;

    try {
      const geometry = typeof (field as any).geometry === 'string'
        ? JSON.parse((field as any).geometry)
        : (field as any).geometry;

      return {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {
            marknr: (field as any).marknr,
            areal: (field as any).areal,
            isOutline: true
          },
          geometry: geometry
        }]
      };
    } catch (e) {
      console.error('Error parsing field geometry:', e);
      return null;
    }
  }, [activeGeoJson, field]);

  const handleMapSelect = (mapId: number) => {
    setActiveMapId(mapId);
  };

  const handleZonesChange = (updatedZones: ZoneData[]) => {
    setMaps(prev => prev.map((m: any) => {
      if ((m as any).Id !== activeMapId) return m;

      // FIX: Handle microservice format - update zones in FeatureCollection features
      if ((m as any).Zones?.type === 'FeatureCollection' && (m as any).Zones.features) {
        const updatedFeatures = (m as any).Zones.features.map((f: any) => {
          const updatedZone = updatedZones.find(z => z.zone === f.properties?.zone);
          if (updatedZone) {
            return {
              ...f,
              properties: {
                ...f.properties,
                rate: updatedZone.rate
              }
            };
          }
          return f;
        });
        return {
          ...m,
          Zones: {
            ...(m as any).Zones,
            features: updatedFeatures
          }
        };
      }

      if (Array.isArray((m as any).Zones)) {
        return { ...m, Zones: updatedZones };
      } else if ((m as any).Zones?.zones) {
        return { ...m, Zones: { ...(m as any).Zones, zones: updatedZones } };
      }
      return m;
    }));
  };

  // Haandter kort sletning
  const handleMapDelete = useCallback((mapId: number, deleteType: 'hard' | 'soft') => {
    console.log('handleMapDelete:', mapId, deleteType);

    // Vis besked
    const message = deleteType === 'hard'
      ? 'Kortet er slettet permanent'
      : 'Kortet er flyttet til papirkurven';
    setDeleteMessage(message);

    // Fjern besked efter 3 sekunder
    setTimeout(() => {
      setDeleteMessage(null);
    }, 3000);

    // Fjern kortet fra listen
    setMaps(prevMaps => {
      const newMaps = prevMaps.filter(m => m.Id !== mapId);

      // Hvis det slettede kort var aktivt, vaelg et nyt
      if (activeMapId === mapId && newMaps.length > 0) {
        setActiveMapId(newMaps[0].Id);
      } else if (newMaps.length === 0) {
        setActiveMapId(null);
      }

      return newMaps;
    });
  }, [activeMapId]);

  // Bestil knap handler
  const handleBestilClick = () => {
    if (onNavigateToBooking) {
      onNavigateToBooking();
    }
  };

  const currentField = useMemo(() => {
    if (field) {
      return {
        id: (field as any).id?.toString() || fieldId || currentMarknr || '',
        marknr: (field as any).marknr || currentMarknr || '',
        areal: (field as any).areal || (maps.length > 0 ? (maps[0] as any).Areal || 0 : 0),
        cvr: (field as any).cvr || (maps.length > 0 ? (maps[0] as any).CVR : ''),
        mapCount: maps.length
      };
    }

    if (maps.length > 0) {
      return {
        id: fieldId || currentMarknr || '',
        marknr: currentMarknr || (maps[0] as any).Marknr || '',
        areal: (maps[0] as any).Areal || 0,
        cvr: (maps[0] as any).CVR || '',
        mapCount: maps.length
      };
    }

    if (fieldId) {
      return {
        id: fieldId,
        marknr: fieldId,
        areal: (field as any)?.areal || 0,
        cvr: (field as any)?.cvr || '',
        mapCount: 0
      };
    }

    return null;
  }, [field, maps, fieldId, currentMarknr]);

  return (
    <div className="mark-detalje-container">
      <aside className="mark-detalje-sidebar">
        <div className="sidebar-header">
          <button className="back-link" onClick={onBack}>
            <span className="back-arrow">&larr;</span>
            Mine marker
          </button>
        </div>

        <div className="sidebar-section">
          <MarkDropdown
            currentField={currentField as any}
            fields={fields as any}
            onFieldChange={onFieldChange}
          />
        </div>

        <div className="sidebar-section">
          <NoteField
            value={note}
            onChange={setNote}
            placeholder="Skriv en note til marken, fx afgrøde..."
          />
        </div>

        <div className="kort-header">
          <h3>Tildelingskort ({maps.length})</h3>
          <button className="bestil-btn" onClick={handleBestilClick}>
            Bestil
          </button>
        </div>

        {/* Delete besked */}
        {deleteMessage && (
          <div className="delete-message">
            <span className="delete-message-icon">&#10003;</span>
            {deleteMessage}
          </div>
        )}

        <div className="kort-liste-container">
          {loading ? (
            <div className="loading-state">
              <p>Indlaeser kort...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={loadMaps} className="retry-btn">
                Proev igen
              </button>
            </div>
          ) : maps.length === 0 ? (
            <div className="empty-state">
              <p>Der er endnu ikke lavet tildelingskort til denne mark.</p>
              <button className="order-btn" onClick={handleBestilClick}>
                Bestil tildelingskort
              </button>
            </div>
          ) : (
            <KortHistorik
              maps={maps as any}
              activeMapId={activeMapId}
              onMapSelect={handleMapSelect}
              onZonesChange={handleZonesChange}
              onMapDelete={handleMapDelete}
            />
          )}
        </div>
      </aside>

      <main className="mark-detalje-map">
        <ZoneMapView key={activeMapId}
          geoJson={activeGeoJson || fieldOutlineGeoJson}
          zones={activeZones}
          zoneColors={activeGeoJson ? ZONE_COLORS : { 0: '#22c55e' }}
          emptyState={!activeGeoJson && !!fieldOutlineGeoJson}
        />

        {activeZones.length > 0 && (
          <ZoneLegend
            zones={activeZones as any}
            zoneColors={ZONE_COLORS}
            mapType={(activeMap as any)?.Type || 'NDVI'}
          />
        )}
      </main>

      <style>{`
        .mark-detalje-container {
          display: flex;
          height: 100vh;
          background: #f9fafb;
        }

        .mark-detalje-sidebar {
          width: 380px;
          background: #f9fafb;
          border-right: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .sidebar-header {
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          background: white;
        }

        .back-link {
          display: flex;
          align-items: center;
          gap: 8px;
          background: none;
          border: none;
          color: #16a34a;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          padding: 0;
        }

        .back-link:hover {
          color: #15803d;
        }

        .back-arrow {
          font-size: 18px;
        }

        .sidebar-section {
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          background: white;
        }

        .kort-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          background: #f9fafb;
        }

        .kort-header h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .bestil-btn {
          padding: 6px 12px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 13px;
          color: #16a34a;
          cursor: pointer;
          transition: all 0.2s;
        }

        .bestil-btn:hover {
          background: #f0fdf4;
          border-color: #16a34a;
        }

        .delete-message {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 20px 12px;
          padding: 10px 14px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          color: #16a34a;
          font-size: 14px;
          font-weight: 500;
          animation: slideIn 0.2s ease;
        }

        .delete-message-icon {
          font-size: 16px;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .kort-liste-container {
          flex: 1;
          overflow-y: auto;
          padding: 0 20px 20px;
        }

        .loading-state,
        .error-state,
        .empty-state {
          padding: 40px 20px;
          text-align: center;
          color: #6b7280;
        }

        .empty-state p {
          margin-bottom: 16px;
        }

        .order-btn {
          padding: 10px 20px;
          background: #16a34a;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
        }

        .order-btn:hover {
          background: #15803d;
        }

        .retry-btn {
          margin-top: 12px;
          padding: 8px 16px;
          background: #16a34a;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }

        .retry-btn:hover {
          background: #15803d;
        }

        .mark-detalje-map {
          flex: 1;
          position: relative;
          background: #e5e7eb;
        }

        .mark-detalje-map .leaflet-container {
          height: 100%;
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default MarkDetaljePage;
